import React, { useEffect } from 'react';
import { Formik } from 'formik';
import { Box, Button, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useDispatch, useSelector } from 'react-redux';

import SimpleField from '../../common/SimpleField';
import { Spinner } from '../../common/Spinner/Spinner';
import styles from './CounterpartyFormModal.module.scss';
import {
  postAddCounterpartyRequest,
  postAddCounterpartySuccess,
  putUpdateCounterpartyRequest,
  putUpdateCounterpartySuccess,
} from './services/actions';
import { ICounterparty, ICounterpartyCreateRequest } from './services/interfaces';
import { counterpartySelector } from './services/selector';

const modalSx = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 520,
  bgcolor: '#ECEFF1 !important',
  boxShadow: 24,
  p: 4,
};

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  onClose: () => void;
  onDeleteClick: (row: ICounterparty) => void;
}

export const CounterpartyFormModal: React.FC<Props> = ({ isOpen, mode, onClose, onDeleteClick }) => {
  const dispatch = useDispatch();

  const { counterpartyDetails, modalLoading, newCounterparty, updateCounterpartyResponse } =
    useSelector(counterpartySelector);

  const initialValues: ICounterpartyCreateRequest = counterpartyDetails
    ? {
        gln: counterpartyDetails.gln,
        shortName: counterpartyDetails.shortName,
        fullName: counterpartyDetails.fullName,
      }
    : {
        gln: '',
        shortName: '',
        fullName: '',
      };

  const isValid = (v: ICounterpartyCreateRequest) =>
    v.gln.trim().length === 13 && v.shortName.trim().length > 0 && v.fullName.trim().length > 0;

  const onSaveHandler = (values: ICounterpartyCreateRequest) => {
    const body = {
      gln: values.gln.trim(),
      shortName: values.shortName.trim(),
      fullName: values.fullName.trim(),
    };

    if (mode === 'edit' && counterpartyDetails) {
      dispatch(putUpdateCounterpartyRequest({ id: counterpartyDetails.id, ...body }));
    } else {
      dispatch(postAddCounterpartyRequest(body));
    }
  };

  useEffect(() => {
    if (newCounterparty) {
      dispatch(postAddCounterpartySuccess(null));
      onClose();
    }

    if (updateCounterpartyResponse) {
      dispatch(putUpdateCounterpartySuccess(null));
      onClose();
    }
  }, [newCounterparty, updateCounterpartyResponse, dispatch, onClose]);

  const isEditReady = mode === 'create' || Boolean(counterpartyDetails);

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="counterparty-modal-title">
      <Box sx={modalSx}>
        <div className={styles.menu}>
          <span className={styles.spinnerWrapper}>
            <Typography id="counterparty-modal-title" variant="h6" component="h2">
              {mode === 'create' ? 'Новий контрагент' : 'Редагування контрагента'}
            </Typography>
            {modalLoading ? <Spinner /> : null}
          </span>
          <div className={styles.actions}>
            {mode === 'edit' && counterpartyDetails && (
              <DeleteOutlineIcon
                className={styles.icon}
                titleAccess="Видалити"
                onClick={() => {
                  onDeleteClick(counterpartyDetails);
                }}
              />
            )}
            <CloseIcon className={styles.icon} onClick={onClose} />
          </div>
        </div>

        {mode === 'edit' && counterpartyDetails ? (
          <span className={styles.idHint}>ID: {counterpartyDetails.id}</span>
        ) : null}

        {isEditReady ? (
          <Formik initialValues={initialValues} enableReinitialize onSubmit={onSaveHandler}>
            {({ handleSubmit, values }) => (
              <form onSubmit={handleSubmit}>
                <div className={styles.header}>
                  <div className={styles.wrapper}>
                    <span className={styles.title}>
                      GLN (повинен мати 13 символів) <em className={styles.required}>*</em>
                    </span>
                    <SimpleField name="gln" hasMarginTop={false} maxLength={13} validate value={values.gln} />
                  </div>
                  <div className={styles.wrapper}>
                    <span className={styles.title}>
                      Назва <em className={styles.required}>*</em>
                    </span>
                    <SimpleField
                      name="shortName"
                      hasMarginTop={false}
                      maxLength={100}
                      validate
                      value={values.shortName}
                    />
                  </div>
                  <div className={styles.wrapper}>
                    <span className={styles.title}>
                      Адреса <em className={styles.required}>*</em>
                    </span>
                    <SimpleField
                      name="fullName"
                      hasMarginTop={false}
                      maxLength={150}
                      validate
                      value={values.fullName}
                    />
                  </div>
                </div>

                <section className={styles.btnSection}>
                  <Button
                    type="submit"
                    className={styles.saveBtn}
                    disabled={!isValid(values) || modalLoading}
                    variant="contained"
                  >
                    <span className={styles.btnText}>Зберегти</span>
                    <SaveIcon className={styles.icon} />
                  </Button>
                </section>
              </form>
            )}
          </Formik>
        ) : null}
      </Box>
    </Modal>
  );
};
